import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../utils/api';
import {
  Package,
  DollarSign,
  CheckCircle,
  Clock,
  Users,
  UserPlus,
  Truck,
  XCircle,
  TrendingUp,
  Link2,
  Palette,
  CreditCard,
  Eye,
  Copy,
  CalendarDays,
  CalendarRange,
  Calendar,
  Wrench,
} from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Color map matching the orders page status badges
const STATUS_COLORS = {
  ordered: '#3B82F6',     // blue-500
  processing: '#F59E0B',  // amber-500
  dispatched: '#8B5CF6',  // violet-500
  delivered: '#10B981',   // emerald-500
  completed: '#10B981',
  cancelled: '#EF4444',   // red-500
  refunded: '#6B7280',    // gray-500
  pending: '#FBBF24',     // amber-400
};

const STATUS_LABELS = {
  ordered: 'Ordered',
  processing: 'Processing',
  dispatched: 'Dispatched',
  delivered: 'Delivered',
  completed: 'Completed',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
  pending: 'Pending',
};

function formatCurrency(amount, currency = 'USD') {
  const value = Number(amount || 0);
  if (currency === 'USD') return `$${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
  if (currency === 'INR') return `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  return `${currency} ${value.toFixed(2)}`;
}

function formatDateTime(s) {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
}

/* --------------------------------- Charts -------------------------------- */

function StatusDonut({ data }) {
  // data: [{ key, label, value, color }]
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const size = 180;
  const radius = 70;
  const stroke = 24;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * radius;

  if (total === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[220px] text-gray-400 text-sm">
        No data yet
      </div>
    );
  }

  let cumulative = 0;
  const segments = data
    .filter((d) => d.value > 0)
    .map((d) => {
      const fraction = d.value / total;
      const dasharray = `${fraction * circumference} ${circumference}`;
      const dashoffset = -cumulative * circumference;
      cumulative += fraction;
      return { ...d, dasharray, dashoffset };
    });

  return (
    <div className="flex flex-col md:flex-row items-center gap-6">
      <svg width={size} height={size} className="-rotate-90">
        {/* Track */}
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke="#F3F4F6"
          strokeWidth={stroke}
        />
        {segments.map((seg) => (
          <circle
            key={seg.key}
            cx={cx}
            cy={cy}
            r={radius}
            fill="none"
            stroke={seg.color}
            strokeWidth={stroke}
            strokeDasharray={seg.dasharray}
            strokeDashoffset={seg.dashoffset}
            strokeLinecap="butt"
          />
        ))}
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="middle"
          transform={`rotate(90 ${cx} ${cy})`}
          className="fill-navy"
          style={{ fontSize: '24px', fontWeight: 700 }}
        >
          {total}
        </text>
        <text
          x={cx}
          y={cy + 22}
          textAnchor="middle"
          dominantBaseline="middle"
          transform={`rotate(90 ${cx} ${cy})`}
          className="fill-gray-500"
          style={{ fontSize: '11px' }}
        >
          orders
        </text>
      </svg>

      <div className="flex-1 w-full space-y-2">
        {data
          .filter((d) => d.value > 0)
          .map((d) => {
            const pct = total === 0 ? 0 : Math.round((d.value / total) * 100);
            return (
              <div key={d.key} className="flex items-center gap-3 text-sm">
                <span
                  className="w-3 h-3 rounded-full shrink-0"
                  style={{ backgroundColor: d.color }}
                />
                <span className="flex-1 text-gray-700">{d.label}</span>
                <span className="font-semibold text-navy">{d.value}</span>
                <span className="text-gray-400 w-10 text-right">{pct}%</span>
              </div>
            );
          })}
      </div>
    </div>
  );
}

function DailyBars({ series, currency = 'USD' }) {
  // Two metrics per day: orders (blue) and revenue (green) — normalized independently.
  const maxOrders = Math.max(1, ...series.map((d) => d.orders));
  const maxRevenue = Math.max(1, ...series.map((d) => d.revenue));

  const totalOrders = series.reduce((s, d) => s + d.orders, 0);
  const totalRevenue = series.reduce((s, d) => s + d.revenue, 0);

  return (
    <div>
      <div className="flex items-center gap-6 mb-4 text-xs text-gray-600">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm bg-blue-500" />
          <span>Orders</span>
          <span className="font-semibold text-navy">({totalOrders})</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-sm bg-emerald-500" />
          <span>Revenue</span>
          <span className="font-semibold text-navy">
            ({formatCurrency(totalRevenue, currency)})
          </span>
        </div>
      </div>

      <div className="flex items-end gap-1 h-48 border-b border-gray-200">
        {series.map((d) => {
          const oH = (d.orders / maxOrders) * 100;
          const rH = (d.revenue / maxRevenue) * 100;
          return (
            <div
              key={d.date}
              className="flex-1 flex items-end justify-center gap-0.5 group relative h-full"
              title={`${d.label} — Orders: ${d.orders} | Revenue: ${formatCurrency(d.revenue, currency)}`}
            >
              <div
                className="w-1/2 bg-blue-500 rounded-t transition-all hover:bg-blue-600"
                style={{ height: `${oH}%`, minHeight: d.orders > 0 ? '2px' : '0' }}
              />
              <div
                className="w-1/2 bg-emerald-500 rounded-t transition-all hover:bg-emerald-600"
                style={{ height: `${rH}%`, minHeight: d.revenue > 0 ? '2px' : '0' }}
              />

              {/* Tooltip on hover */}
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full bg-navy text-white text-xs rounded-md px-2 py-1 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                <div className="font-semibold">{d.label}</div>
                <div>Orders: {d.orders}</div>
                <div>Revenue: {formatCurrency(d.revenue, currency)}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex gap-1 mt-2">
        {series.map((d, idx) => (
          <div
            key={d.date}
            className="flex-1 text-center text-[10px] text-gray-500"
          >
            {idx % 2 === 0 ? d.label : ''}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------- Stat Card ------------------------------- */

function StatCard({ icon: Icon, label, value, accent = 'blue', subtitle }) {
  const accents = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    purple: 'bg-purple-100 text-purple-600',
    red: 'bg-red-100 text-red-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    violet: 'bg-violet-100 text-violet-600',
    amber: 'bg-amber-100 text-amber-600',
    gray: 'bg-gray-100 text-gray-600',
    indigo: 'bg-indigo-100 text-indigo-600',
  };

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm hover:shadow-md transition">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-11 h-11 rounded-full flex items-center justify-center ${accents[accent] || accents.blue}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
        {label}
      </h3>
      <p className="text-2xl font-bold text-navy">{value}</p>
      {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
    </div>
  );
}

/* ------------------------------- Component ------------------------------- */

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reconciling, setReconciling] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await apiRequest(`${BACKEND_URL}/api/admin/stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch stats');
      }

      setStats(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const statusChartData = useMemo(() => {
    if (!stats?.orders_by_status) return [];
    return Object.entries(stats.orders_by_status).map(([key, value]) => ({
      key,
      label: STATUS_LABELS[key] || (key.charAt(0).toUpperCase() + key.slice(1)),
      value,
      color: STATUS_COLORS[key] || '#6B7280',
    }));
  }, [stats]);

  const currency = stats?.currency || 'USD';

  const handleReconcile = async () => {
    const input = window.prompt(
      'Reconcile all orders — enter the item price to apply to EVERY order (this overwrites the amount on all existing orders).\n\nExample: 39'
    );
    if (input === null) return; // cancelled
    const value = parseFloat(input);
    if (Number.isNaN(value) || value < 0) {
      window.alert('Please enter a valid non-negative number.');
      return;
    }
    if (
      !window.confirm(
        `This will set amount = $${value} on EVERY order in the database. This action cannot be undone.\n\nProceed?`
      )
    ) {
      return;
    }
    setReconciling(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await apiRequest(`${BACKEND_URL}/api/admin/orders/reconcile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ amount: value }),
      });
      if (!res.ok) {
        throw new Error(res.data?.detail || 'Reconcile failed');
      }
      window.alert(res.data?.message || 'Orders reconciled.');
      fetchStats();
    } catch (err) {
      window.alert(err.message || 'Reconcile failed');
    } finally {
      setReconciling(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 md:p-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-navy">Dashboard</h1>
          <p className="text-sm text-gray-500">Overview of your store's performance</p>
        </div>

        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading statistics...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {stats && (
          <>
            {/* Top stat cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <StatCard
                icon={Package}
                label="Total Orders"
                value={stats.total_orders}
                accent="blue"
              />
              <StatCard
                icon={DollarSign}
                label="Total Revenue"
                value={formatCurrency(stats.total_revenue, currency)}
                accent="purple"
                subtitle="Paid orders only"
              />
              <StatCard
                icon={Users}
                label="Total Users"
                value={stats.total_users}
                accent="indigo"
              />
              <StatCard
                icon={UserPlus}
                label="New Users (7d)"
                value={stats.new_users_7d}
                accent="emerald"
                subtitle="Last 7 days"
              />
              <StatCard
                icon={Clock}
                label="Pending"
                value={stats.pending_orders}
                accent="amber"
                subtitle="Ordered / Processing"
              />
              <StatCard
                icon={Truck}
                label="Dispatched"
                value={stats.dispatched_orders}
                accent="violet"
              />
              <StatCard
                icon={CheckCircle}
                label="Delivered"
                value={stats.completed_orders}
                accent="green"
              />
              <StatCard
                icon={XCircle}
                label="Cancelled"
                value={stats.cancelled_orders}
                accent="red"
              />
            </div>

            {/* Revenue breakdown */}
            {stats.revenue_breakdown && (
              <div className="bg-white rounded-2xl p-6 shadow-sm mb-6">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <div>
                    <h2 className="text-lg font-bold text-navy flex items-center gap-2">
                      <DollarSign className="w-5 h-5" />
                      Revenue Breakdown
                    </h2>
                    <p className="text-xs text-gray-500">
                      Paid orders only · {stats.revenue_breakdown.paid_orders} paid order{stats.revenue_breakdown.paid_orders === 1 ? '' : 's'} so far
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {[
                    { key: 'daily',   Icon: CalendarDays,  title: 'Daily',   accent: 'from-blue-500 to-blue-600' },
                    { key: 'weekly',  Icon: CalendarRange, title: 'Weekly',  accent: 'from-indigo-500 to-indigo-600' },
                    { key: 'monthly', Icon: Calendar,      title: 'Monthly', accent: 'from-violet-500 to-violet-600' },
                    { key: 'mtd',     Icon: TrendingUp,    title: 'MTD',     accent: 'from-emerald-500 to-emerald-600' },
                    { key: 'ytd',     Icon: TrendingUp,    title: 'YTD',     accent: 'from-amber-500 to-amber-600' },
                  ].map(({ key, Icon, title, accent }) => {
                    const value = stats.revenue_breakdown?.[key] || 0;
                    const range = stats.revenue_breakdown?.ranges?.[key];
                    return (
                      <div
                        key={key}
                        className={`rounded-xl p-4 text-white bg-gradient-to-br ${accent} shadow-sm`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold uppercase tracking-wide opacity-90">
                            {title}
                          </span>
                          <Icon className="w-4 h-4 opacity-80" />
                        </div>
                        <p className="text-2xl font-bold leading-tight">
                          {formatCurrency(value, currency)}
                        </p>
                        {range?.label && (
                          <p className="text-[11px] opacity-80 mt-1">{range.label}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Charts row */}
            <div className="grid lg:grid-cols-3 gap-6 mb-6">
              {/* Daily series — 2/3 width */}
              <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-bold text-navy flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      Last 14 Days
                    </h2>
                    <p className="text-xs text-gray-500">Orders & revenue trend</p>
                  </div>
                </div>
                <DailyBars series={stats.daily_series || []} currency={currency} />
              </div>

              {/* Status donut — 1/3 width */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="text-lg font-bold text-navy mb-4">Orders by Status</h2>
                <StatusDonut data={statusChartData} />
              </div>
            </div>

            {/* Recent orders + Top products */}
            <div className="grid lg:grid-cols-3 gap-6 mb-6">
              {/* Recent orders */}
              <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-navy">Recent Orders</h2>
                  <button
                    onClick={() => navigate('/admin/orders')}
                    className="text-sm text-navy hover:underline font-medium"
                  >
                    View all →
                  </button>
                </div>

                {(!stats.recent_orders || stats.recent_orders.length === 0) ? (
                  <div className="text-center text-gray-500 text-sm py-8">
                    No orders yet
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-xs text-gray-500 uppercase border-b border-gray-200">
                        <tr>
                          <th className="text-left py-2 pr-3">Order</th>
                          <th className="text-left py-2 pr-3">Customer</th>
                          <th className="text-left py-2 pr-3">Status</th>
                          <th className="text-right py-2 pr-3">Amount</th>
                          <th className="text-right py-2">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {stats.recent_orders.map((o) => {
                          const status = (o.order_status || 'ordered').toLowerCase();
                          const color = STATUS_COLORS[status] || '#6B7280';
                          return (
                            <tr key={o.transaction_id} className="hover:bg-gray-50">
                              <td className="py-3 pr-3">
                                <div className="font-medium text-navy">
                                  #{(o.transaction_id || '').slice(0, 8)}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {o.product_name}
                                </div>
                                <div className="text-xs text-gray-400">
                                  {formatDateTime(o.created_at)}
                                </div>
                              </td>
                              <td className="py-3 pr-3 text-gray-700">
                                <div>{o.customer_name || '—'}</div>
                                <div className="text-xs text-gray-500">
                                  {o.customer_email || ''}
                                </div>
                              </td>
                              <td className="py-3 pr-3">
                                <span
                                  className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold"
                                  style={{
                                    backgroundColor: `${color}20`,
                                    color: color,
                                  }}
                                >
                                  {STATUS_LABELS[status] || status}
                                </span>
                              </td>
                              <td className="py-3 pr-3 text-right font-semibold text-navy">
                                {formatCurrency(o.amount, currency)}
                              </td>
                              <td className="py-3 text-right">
                                <button
                                  onClick={() => navigate(`/admin/orders/${o.transaction_id}`)}
                                  className="text-navy hover:text-navy/80 inline-flex items-center gap-1 text-xs font-medium"
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                  View
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Top products */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="text-lg font-bold text-navy mb-4">Top Products</h2>
                {(!stats.top_products || stats.top_products.length === 0) ? (
                  <div className="text-center text-gray-500 text-sm py-8">
                    No products yet
                  </div>
                ) : (
                  <div className="space-y-3">
                    {stats.top_products.map((p, idx) => {
                      const max = stats.top_products[0].count || 1;
                      const pct = (p.count / max) * 100;
                      return (
                        <div key={p.name + idx}>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="font-medium text-gray-700 truncate pr-2">
                              {idx + 1}. {p.name}
                            </span>
                            <span className="font-semibold text-navy">{p.count}</span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-navy rounded-full transition-all"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Catalog counters */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
              <StatCard
                icon={Link2}
                label="Dynamic Links"
                value={stats.total_dynamic_links}
                accent="blue"
              />
              <StatCard
                icon={Palette}
                label="Artists"
                value={stats.total_artists}
                accent="purple"
              />
              <StatCard
                icon={CreditCard}
                label="Digital Cards"
                value={stats.total_digital_cards}
                accent="emerald"
              />
              <StatCard
                icon={CreditCard}
                label="Review Cards"
                value={stats.total_review_cards}
                accent="amber"
              />
              <StatCard
                icon={CreditCard}
                label="Review Standees"
                value={stats.total_review_standees}
                accent="violet"
              />
              <StatCard
                icon={Copy}
                label="Card Requests"
                value={stats.total_card_requests}
                accent="indigo"
              />
            </div>

            {/* Quick Actions (kept) */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-lg font-bold text-navy mb-4">Quick Actions</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <button
                  onClick={() => navigate('/admin/orders')}
                  className="flex items-center gap-4 p-4 border-2 border-gray-200 rounded-xl hover:border-navy transition-colors text-left"
                >
                  <Package className="w-7 h-7 text-navy" />
                  <div>
                    <h3 className="font-semibold text-navy">View All Orders</h3>
                    <p className="text-sm text-gray-600">Manage and track orders</p>
                  </div>
                </button>

                <button
                  onClick={() => navigate('/admin/users')}
                  className="flex items-center gap-4 p-4 border-2 border-gray-200 rounded-xl hover:border-navy transition-colors text-left"
                >
                  <Users className="w-7 h-7 text-navy" />
                  <div>
                    <h3 className="font-semibold text-navy">Manage Users</h3>
                    <p className="text-sm text-gray-600">View customer accounts</p>
                  </div>
                </button>

                <button
                  onClick={() => navigate('/')}
                  className="flex items-center gap-4 p-4 border-2 border-gray-200 rounded-xl hover:border-navy transition-colors text-left"
                >
                  <div className="w-7 h-7 text-navy flex items-center justify-center text-xl">🏠</div>
                  <div>
                    <h3 className="font-semibold text-navy">View Website</h3>
                    <p className="text-sm text-gray-600">Go to customer site</p>
                  </div>
                </button>

              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
